import React from "react";
import QuizViewer, { getLatestQuizResults } from "../QuizViewer";
import { useQuizAttempts } from "./useQuizAttempts";
import RetakeQuizButton from "./RetakeQuizButton";

/**
 * Thin composition wrapper around the existing (untouched) QuizViewer flow that adds
 * Max Attempts awareness: shows attempts used/remaining, and lets the student retake
 * the quiz while attempts remain. QuizViewer/QuizStart/ReadyForQuiz/QuizResult keep
 * their original single-attempt behavior; quizzes without a configured `maxAttempts`
 * render exactly as before (this wrapper is fully inert for them).
 */
const QuizViewerWithAttempts = ({
  activeContent,
  courseDetails,
  setActiveContent,
  quizStatus,
  setQuizStatus,
  quizStartedAt,
  setQuizStartedAt,
}) => {
  // The stored content record already tells us directly whether the last attempt passed
  // (`quizResults.passed`) — trust that instead of recomputing from score fields, since
  // `quizResults.score` is a raw count, not a percentage, and varies by question weighting.
  // Max-Attempts records don't populate top-level `quizResults` at all (only
  // `maxAttemptsHistory`), so resolve via the same fallback QuizViewer itself uses.
  const initialHasPassed = Boolean(getLatestQuizResults(activeContent)?.passed);

  // A fresh submission's evaluateAnswer response has no `passed` field, only percentage_score —
  // compare that against the quiz's passing bar instead.
  const passingBar = Number(activeContent?.passingPercentage ?? NaN);
  const computePassed = (percentageScore) => {
    const pct = Number(percentageScore ?? NaN);
    return Number.isFinite(pct) && Number.isFinite(passingBar) && pct >= passingBar;
  };

  const {
    maxAttempts,
    attemptsUsed,
    remainingAttempts,
    canRetake,
    hasPassed,
    isRetaking,
    startRetake,
    recordAttempt,
  } = useQuizAttempts({
    maxAttempts: activeContent?.maxAttempts,
    // `maxAttemptsUsed` is the Max-Attempts-specific counter — distinct from the general,
    // pre-existing `attemptCount` field used elsewhere for unrelated attempt reporting.
    // Falls back to `attemptCount` only until the backend has rolled out the new field.
    attemptsUsed: activeContent?.maxAttemptsUsed ?? activeContent?.attemptCount ?? 0,
    hasPassed: initialHasPassed,
  });

  const effectiveActiveContent = isRetaking
    ? { ...activeContent, completed: false }
    : activeContent;

  const showResults = (activeContent?.completed || quizStatus === "finish") && !isRetaking;

  const handleRetake = () => {
    startRetake();
    setQuizStatus("");
    setQuizStartedAt(null);
  };

  // Bridges QuizViewer's fresh evaluateAnswer response into the pass/fail-aware
  // attempt tracker; auto-submits (refresh/offline) call this with no data, which
  // safely defaults to "not passed" rather than guessing.
  const handleAttemptSubmitted = (responseData) => {
    recordAttempt(computePassed(responseData?.percentage_score));
  };

  return (
    <>
      <QuizViewer
        courseDetails={courseDetails}
        activeContent={effectiveActiveContent}
        setActiveContent={setActiveContent}
        quizStatus={quizStatus}
        setQuizStatus={setQuizStatus}
        quizStartedAt={quizStartedAt}
        setQuizStartedAt={setQuizStartedAt}
        isMaxAttemptsQuiz={Boolean(maxAttempts)}
        attemptNumber={maxAttempts ? attemptsUsed + 1 : undefined}
        maxAttemptsLimit={maxAttempts}
        remainingAttempts={remainingAttempts}
        onAttemptSubmitted={handleAttemptSubmitted}
      />
      {showResults && (
        <RetakeQuizButton
          canRetake={canRetake}
          remainingAttempts={remainingAttempts}
          onRetake={handleRetake}
        />
      )}
    </>
  );
};

export default QuizViewerWithAttempts;
